"use client";

import { useEffect, useState } from "react";
import { QuizStats, QuizQuestion } from "@/lib/daily/types";
import { generateQuizShare, generateShareCardCanvas } from "@/lib/daily/shareGenerator";
import ShareButton from "../shared/ShareButton";
import { Award, BookOpen } from "lucide-react";
import Link from "next/link";
import QuizStatsComponent from "./QuizStats";

interface QuizResultsProps {
  readonly questions: readonly QuizQuestion[];
  readonly answers: Record<string, number>;
  readonly stats: QuizStats;
  readonly mode?: "daily" | "practice";
}

function formatToolTitle(url: string): string {
  const segment = url.split("/").pop() || "";
  return segment
    .split("-")
    .map((w) => {
      const lower = w.toLowerCase();
      if (lower === "vs") return "vs.";
      if (lower === "etf") return "ETF";
      if (lower === "sip") return "SIP";
      if (lower === "fd") return "FD";
      if (lower === "hsa") return "HSA";
      if (lower === "fsa") return "FSA";
      if (lower === "rrsp") return "RRSP";
      if (lower === "tfsa") return "TFSA";
      if (lower === "gic") return "GIC";
      if (lower === "nps") return "NPS";
      if (lower === "cpf") return "CPF";
      if (lower === "cd") return "CD";
      if (lower === "hysa") return "HYSA";
      return w.charAt(0).toUpperCase() + w.slice(1);
    })
    .join(" ");
}

export default function QuizResults({ questions, answers, stats, mode = "daily" }: QuizResultsProps) {
  const [countdown, setCountdown] = useState("");

  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextMidnight = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
      );
      const diff = nextMidnight.getTime() - now.getTime();

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setCountdown(
        `${hours}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(2, "0")}s`
      );
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  let correctCount = 0;
  questions.forEach((q) => {
    if (answers[q.id] === q.correctIndex) {
      correctCount += 1;
    }
  });

  const totalCount = questions.length;
  const scorePercent = Math.round((correctCount / totalCount) * 100);

  // Identify best category from today's performance
  const bestCategory = questions.find((q) => answers[q.id] === q.correctIndex)?.category || "general";

  // Share Text
  const shareText = generateQuizShare(correctCount, totalCount, stats.currentStreak, bestCategory.toUpperCase());

  // Share Image Canvas Data URL
  const shareCanvasDataUrl = generateShareCardCanvas(
    "Finance Quiz of the Day",
    `Score: ${correctCount}/${totalCount} (${scorePercent}%)`,
    "Quiz Streak",
    `${stats.currentStreak} days`,
    [
      `Total Quizzes: ${stats.quizzesTaken}`,
      `Total Questions: ${stats.totalQuestions}`,
      `Correct Answers: ${stats.correctAnswers}`,
    ]
  );

  // Recommendations come only from today's questions. Most days none of the
  // selected questions carry a relatedCalculator — in that case the whole
  // column is omitted (never render a heading over an empty pane). Dedupe by
  // href so two questions pointing at the same tool don't produce twin cards.
  const recommendations = questions.filter(
    (q, i) => q.relatedCalculator && questions.findIndex((p) => p.relatedCalculator === q.relatedCalculator) === i
  );
  const hasRecommendations = recommendations.length > 0;

  return (
    <div className="quiz-results-card anim-fade-in">
      <div className="quiz-results-grid" style={hasRecommendations ? undefined : { gridTemplateColumns: "1fr" }}>
        {/* Left Column: Score, Headline, Summary, Stats, Countdown & Share */}
        <div className="quiz-results-left">
          <div style={{ display: "flex", alignItems: "baseline", gap: "12px", marginBottom: "0.25rem" }}>
            <div className="quiz-results-score">{correctCount}/{totalCount}</div>
            <h3 className="quiz-results-headline">
              {scorePercent === 100
                ? "Perfect Score! 🌟"
                : scorePercent >= 75
                ? "Great Job! 👍"
                : "Good Effort! 📚"}
            </h3>
          </div>
          <p className="quiz-results-summary">
            You answered {correctCount} out of {totalCount} questions correctly. Your current streak is{" "}
            <strong>{stats.currentStreak} days</strong>.
          </p>

          {/* Stats Tracker */}
          <QuizStatsComponent stats={stats} />

          {/* Confetti element if perfect score */}
          {scorePercent === 100 && (
            <div style={{ color: "var(--accent-orange)", fontWeight: "var(--fw-extrabold)", margin: "0.5rem 0", fontSize: "var(--text-xs)" }}>
              🎉 Perfect 100%! Double streak points! 🎉
            </div>
          )}

          {/* Countdown & Share Row */}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "1rem",
              paddingTop: "0.75rem",
              marginTop: "0.75rem",
              borderTop: "1px solid var(--border-color)",
            }}
          >
            {mode === "daily" ? (
              <>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: "var(--text-2xs)", fontWeight: "var(--fw-bold)", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Next Quiz In
                  </div>
                  <div style={{ fontSize: "var(--text-xl)", fontWeight: "var(--fw-extrabold)", color: "var(--text-primary)", fontFamily: "monospace", marginTop: "2px" }}>
                    {countdown}
                  </div>
                </div>

                <ShareButton
                  text={shareText}
                  canvasDataUrl={shareCanvasDataUrl}
                  url="https://financelives.com/daily/quiz"
                />
              </>
            ) : (
              <button
                onClick={() => {
                  const pid = Date.now();
                  window.location.href = `/daily/quiz?mode=practice&pid=${pid}`;
                }}
                style={{
                  backgroundColor: "var(--info)",
                  color: "var(--text-on-accent)",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "var(--radius-full)",
                  fontWeight: "var(--fw-bold)",
                  fontSize: "var(--text-sm)",
                  cursor: "pointer",
                  width: "100%",
                }}
              >
                Play Practice Quiz Again
              </button>
            )}
          </div>
        </div>

        {/* Right Column: rendered ONLY when at least one recommendation resolves */}
        {hasRecommendations && (
        <div className="quiz-results-right">
          <h4 style={{ fontWeight: "var(--fw-extrabold)", fontSize: "var(--text-2xs)", textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em", textAlign: "left", margin: "0 0 8px 0" }}>
            Recommended Calculators & Guides
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {recommendations.map((q) => {
              if (!q.relatedCalculator) return null;
              const isCompare = q.relatedCalculator.startsWith("/compare");
              const categoryLabel = q.category.toUpperCase();
              const toolTitle = formatToolTitle(q.relatedCalculator);
              return (
                <Link
                  key={q.id}
                  href={q.relatedCalculator}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 14px",
                    textAlign: "left",
                    textDecoration: "none",
                    color: "var(--text-primary)",
                    backgroundColor: "var(--bg-surface-hover)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "var(--text-xs)",
                    fontWeight: "var(--fw-semibold)",
                    transition: "all var(--transition-fast)",
                  }}
                >
                  <BookOpen size={16} style={{ color: "var(--primary)", flexShrink: 0 }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: "2px", overflow: "hidden" }}>
                    <span style={{ fontSize: "var(--text-2xs)", fontWeight: "var(--fw-extrabold)", color: "var(--success-fg)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                      {categoryLabel} {isCompare ? "COMPARISON" : "CALCULATOR"}
                    </span>
                    <span style={{ fontSize: "var(--text-xs)", fontWeight: "var(--fw-bold)", color: "var(--text-primary)", lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {toolTitle} {isCompare || toolTitle.toLowerCase().includes("calculator") ? "" : "Calculator"}
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
